import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, apiError, money, API } from "@/lib/api";
import { Wordmark, NumField, PinPad, SignaturePad } from "@/components/Brand";
import IdleGuard from "@/components/IdleGuard";
import { toast } from "sonner";
import { Camera, Trash2, Check, AlertTriangle, ArrowRight, ArrowLeft, X, Download } from "lucide-react";

const DENOMS = [["100", 100], ["50", 50], ["20", 20], ["10", 10], ["5", 5], ["2", 2], ["1", 1]];
const STEPS = ["Setup", "Counting", "CashVerification", "Review", "Signatures", "Locked"];
const STEP_LABELS = { Setup: "Setup", Counting: "Checks", CashVerification: "Cash Count", Review: "Review", Signatures: "Deposit & Sign", Locked: "Complete" };

export default function CountFlow() {
  const { sessionId } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [s, setS] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/sessions/${sessionId}`);
      setS(data);
    } catch (e) {
      toast.error(apiError(e));
      nav("/count");
    }
  }, [sessionId, nav]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const warn = (e) => { if (s && !s.locked) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [s]);

  if (!s) return <div className="min-h-screen flex items-center justify-center text-[color:var(--muted)]">Loading session…</div>;

  const stepIdx = STEPS.indexOf(s.state);

  return (
    <IdleGuard username={user.username}>
      <div className="min-h-screen pad-safe">
        <header className="p-5 border-b border-[color:var(--line)] bg-white/70 backdrop-blur sticky top-0 z-10">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <button className="btn btn-ghost h-9 px-3" onClick={() => nav("/count")} data-testid="exit-flow"><ArrowLeft size={16} /></button>
              <div>
                <div className="font-semibold">{s.service} · {s.date}</div>
                <div className="text-xs text-[color:var(--muted)]">Counter 1: {s.counter1_name}{s.counter2_name ? ` · Counter 2: ${s.counter2_name}` : ""}</div>
              </div>
            </div>
            <Wordmark sub={false} className="hidden sm:block" />
          </div>
          <div className="flex gap-1">
            {STEPS.map((st, i) => (
              <div key={st} className={`h-1.5 flex-1 rounded-full ${i <= stepIdx ? "bg-[color:var(--olive)]" : "bg-[color:var(--line)]"}`} title={STEP_LABELS[st]} />
            ))}
          </div>
          <div className="text-xs text-[color:var(--olive)] font-semibold mt-2 uppercase tracking-wider">{STEP_LABELS[s.state]}</div>
        </header>

        <main className="max-w-2xl mx-auto p-5 fadeup">
          {s.state === "Setup" && <Setup s={s} reload={load} />}
          {s.state === "Counting" && <Counting s={s} reload={load} />}
          {s.state === "CashVerification" && <CashCount s={s} reload={load} />}
          {s.state === "Review" && <Review s={s} reload={load} />}
          {s.state === "Signatures" && <SignStep s={s} reload={load} />}
          {s.state === "Locked" && <Locked s={s} />}
        </main>
      </div>
    </IdleGuard>
  );
}

/* ---------------- Setup ---------------- */
function Setup({ s, reload }) {
  const [uname, setUname] = useState("");
  const [showPin, setShowPin] = useState(false);

  const addSecond = async (pin) => {
    try {
      await api.post(`/sessions/${s.id}/second-counter`, { username: uname.trim().toLowerCase(), pin });
      toast.success("Counter 2 authenticated");
      setShowPin(false);
      reload();
    } catch (e) { toast.error(apiError(e)); }
  };

  const begin = async () => {
    try { await api.post(`/sessions/${s.id}/begin`); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-5">
      <div className="card p-6">
        <h2 className="font-serif text-2xl mb-1">Two-Person Setup</h2>
        <p className="text-sm text-[color:var(--ink-2)] mb-4">A second, distinct counter must authenticate on this device before counting can begin.</p>
        <div className="flex items-center gap-3 mb-4 p-3 rounded-lg bg-[color:var(--sage-light)]">
          <Check className="text-[color:var(--olive)]" size={18} />
          <div><div className="text-sm font-semibold">Counter 1</div><div className="text-xs text-[color:var(--ink-2)]">{s.counter1_name}</div></div>
        </div>

        {s.counter2_id ? (
          <div className="flex items-center gap-3 p-3 rounded-lg bg-[color:var(--sage-light)]">
            <Check className="text-[color:var(--olive)]" size={18} />
            <div><div className="text-sm font-semibold">Counter 2</div><div className="text-xs text-[color:var(--ink-2)]">{s.counter2_name}</div></div>
          </div>
        ) : !showPin ? (
          <div className="space-y-3">
            <label className="block">
              <span className="label block mb-1.5">Counter 2 Username</span>
              <input className="field" value={uname} onChange={(e) => setUname(e.target.value.replace(/\s/g, ""))} placeholder="e.g. rancho2" autoCapitalize="none" autoCorrect="off" data-testid="counter2-username" />
            </label>
            <button className="btn btn-secondary w-full" disabled={!uname} onClick={() => setShowPin(true)} data-testid="counter2-continue">Enter Counter 2 PIN</button>
          </div>
        ) : (
          <div>
            <p className="text-center text-sm text-[color:var(--ink-2)] mb-3">{uname} — enter PIN</p>
            <PinPad testid="counter2-pinpad" onComplete={addSecond} />
            <button className="btn btn-ghost w-full mt-4 h-10" onClick={() => setShowPin(false)}>Cancel</button>
          </div>
        )}
      </div>

      <button className="btn btn-primary w-full" disabled={!s.counter2_id} onClick={begin} data-testid="begin-count">Begin Counting <ArrowRight size={18} /></button>
    </div>
  );
}

/* ---------------- Checks (Counting) ---------------- */
function Counting({ s, reload }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [amount, setAmount] = useState("");
  const [checkNo, setCheckNo] = useState("");
  const [payer, setPayer] = useState("");
  const [busy, setBusy] = useState(false);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    if (preview) URL.revokeObjectURL(preview);
    setPreview(URL.createObjectURL(f));
  };

  const purge = () => {
    if (preview) URL.revokeObjectURL(preview);
    setPreview(null); setFile(null); setAmount(""); setCheckNo(""); setPayer("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const addCheck = async () => {
    if (!file) { toast.error("A photo is required for every check"); return; }
    if (!amount || Number(amount) <= 0) { toast.error("Enter a valid amount"); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("photo", file);
    fd.append("amount", amount);
    fd.append("check_number", checkNo);
    fd.append("payer_last", payer);
    try {
      const { data } = await api.post(`/sessions/${s.id}/checks`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data && data._last_check_readable === false) toast.warning("Photo looks blurry/low-contrast — consider retaking for a clearer record");
      else toast.success("Check added");
      purge(); // purge client-side cached image after confirmed upload
      reload();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const removeCheck = async (id) => {
    try { await api.delete(`/sessions/${s.id}/checks/${id}`); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const checkTotal = (s.checks || []).reduce((a, c) => a + Number(c.amount || 0), 0);

  return (
    <div className="space-y-5">
      <div className="card p-6">
        <h2 className="font-serif text-2xl mb-1">Check Capture</h2>
        <p className="text-sm text-[color:var(--ink-2)] mb-4">Photograph the front of each check. A photo is required — no photo, no entry.</p>

        <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onFile} data-testid="check-photo-input" />

        {!preview ? (
          <button className="w-full h-44 rounded-xl border-2 border-dashed border-[color:var(--line)] flex flex-col items-center justify-center gap-2 text-[color:var(--muted)] active:bg-[color:var(--sage-light)]" onClick={() => fileRef.current?.click()} data-testid="capture-check">
            <Camera size={30} />
            <span className="text-sm font-medium">Tap to photograph check</span>
          </button>
        ) : (
          <div className="relative">
            <img src={preview} alt="check" className="w-full h-52 object-cover rounded-xl border border-[color:var(--line)]" />
            <button className="absolute top-2 right-2 btn btn-danger h-9 w-9 p-0 rounded-full" onClick={purge} data-testid="clear-photo"><X size={16} /></button>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
          <NumField label="Amount" prefix="$" value={amount} onChange={setAmount} testid="check-amount" placeholder="0.00" />
          <label className="block"><span className="label block mb-1.5">Check # (opt)</span><input className="field font-mono" value={checkNo} onChange={(e) => setCheckNo(e.target.value)} data-testid="check-number" /></label>
          <label className="block"><span className="label block mb-1.5">Payer Last (opt)</span><input className="field" value={payer} onChange={(e) => setPayer(e.target.value)} data-testid="check-payer" /></label>
        </div>
        <button className="btn btn-primary w-full mt-4" disabled={busy || !preview} onClick={addCheck} data-testid="add-check">{busy ? "Uploading…" : "Add Check"}</button>
      </div>

      <div className="card p-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold">Checks Entered</h3>
          <div className="text-sm"><span className="text-[color:var(--muted)]">{(s.checks || []).length} checks · </span><span className="font-mono tabnum font-semibold text-[color:var(--olive)]">{money(checkTotal)}</span></div>
        </div>
        {(s.checks || []).length === 0 && <p className="text-sm text-[color:var(--muted)]">No checks yet.</p>}
        <div className="space-y-2">
          {(s.checks || []).map((c) => (
            <div key={c.id} className="flex items-center justify-between p-3 rounded-lg bg-[color:var(--cream)]" data-testid={`check-row-${c.id}`}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-md bg-[color:var(--olive-light)] flex items-center justify-center"><Camera size={16} className="text-[color:var(--olive)]" /></div>
                <div><div className="font-mono tabnum font-semibold">{money(c.amount)}</div><div className="text-xs text-[color:var(--muted)]">{c.check_number ? `#${c.check_number}` : "photo attached"} {c.payer_last}</div></div>
              </div>
              <button className="btn btn-ghost h-9 w-9 p-0" onClick={() => removeCheck(c.id)} data-testid={`remove-check-${c.id}`}><Trash2 size={16} className="text-[color:var(--terracotta)]" /></button>
            </div>
          ))}
        </div>
      </div>

      <div className="card p-6">
        <h3 className="font-semibold text-[color:var(--olive)]">Next: Blind Cash Count</h3>
        <p className="text-sm text-[color:var(--ink-2)]">When checks are complete, Counter 1 enters the cash denominations below, then hands the iPad to Counter 2.</p>
      </div>

      <CashCount s={s} reload={reload} />
    </div>
  );
}

/* ---------------- Blind Cash Count ---------------- */
function CashCount({ s, reload, inlineStart }) {
  const blank = { "100": "", "50": "", "20": "", "10": "", "5": "", "2": "", "1": "" };
  const [denoms, setDenoms] = useState(blank);
  const [coins, setCoins] = useState("");
  const [username, setUsername] = useState("");
  const [showPin, setShowPin] = useState(false);
  const [mismatch, setMismatch] = useState(null);
  const [awaiting, setAwaiting] = useState(false);

  const subtotal = DENOMS.reduce((a, [k, v]) => a + (Number(denoms[k]) || 0) * v, 0) + (Number(coins) || 0);

  const submit = async (pin) => {
    const payload = {
      username: username.trim().toLowerCase(), pin,
      denoms: Object.fromEntries(DENOMS.map(([k]) => [k, Number(denoms[k]) || 0])),
      coins: Number(coins) || 0,
    };
    try {
      const { data } = await api.post(`/sessions/${s.id}/cash`, payload);
      setShowPin(false);
      if (data.status === "awaiting_second") {
        setAwaiting(true);
        setDenoms(blank); setCoins(""); setUsername(""); setMismatch(null);
        toast.info("Counter 1 recorded. Hand the iPad to Counter 2.");
        reload();
      } else if (data.status === "mismatch") {
        setMismatch(data);
        setAwaiting(false);
        setDenoms(blank); setCoins(""); setUsername("");
        toast.error(`Mismatch on: ${data.denominations.join(", ")} — recount required`);
        reload();
      } else if (data.status === "matched") {
        toast.success("Counts match!");
        reload();
      }
    } catch (e) { toast.error(apiError(e)); }
  };

  if (inlineStart && s.state === "Counting") {
    return null;
  }

  return (
    <div className="space-y-5">
      <div className="card p-6">
        <h2 className="font-serif text-2xl mb-1">Blind Cash Count</h2>
        <p className="text-sm text-[color:var(--ink-2)] mb-3">Each counter enters denominations independently. The other's entries stay hidden until both submit.</p>

        {mismatch && (
          <div className="flex items-start gap-3 p-4 rounded-lg bg-[color:var(--terracotta-light)] mb-4" data-testid="mismatch-banner">
            <AlertTriangle className="text-[color:var(--terracotta)] mt-0.5" size={18} />
            <div className="text-sm">
              <div className="font-semibold text-[color:var(--terracotta)]">Recount required (attempt {mismatch.attempt})</div>
              <div className="text-[color:var(--ink-2)]">Disagreement on: {mismatch.denominations.join(", ")}. Both counters must re-enter.</div>
            </div>
          </div>
        )}

        {awaiting && (
          <div className="p-4 rounded-lg bg-[color:var(--olive-light)] mb-4 text-sm font-semibold text-[color:var(--olive)]" data-testid="awaiting-banner">
            Counter 1 has submitted. Counter 2 — enter your independent count below.
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {DENOMS.map(([k, v]) => (
            <NumField key={k} label={`$${k} bills`} value={denoms[k]} onChange={(val) => setDenoms((d) => ({ ...d, [k]: val }))} testid={`cash-${k}`} />
          ))}
          <NumField label="Coins ($)" prefix="$" value={coins} onChange={setCoins} testid="cash-coins" />
        </div>

        <div className="flex items-center justify-between mt-5 p-4 rounded-lg bg-[color:var(--cream)]">
          <span className="label">Your Subtotal</span>
          <span className="font-mono tabnum text-xl font-semibold text-[color:var(--olive)]" data-testid="cash-subtotal">{money(subtotal)}</span>
        </div>

        {!showPin ? (
          <div className="mt-4 space-y-3">
            <label className="block">
              <span className="label block mb-1.5">Your Username (confirm identity)</span>
              <input className="field" value={username} onChange={(e) => setUsername(e.target.value.replace(/\s/g, ""))} placeholder="e.g. rancho1" autoCapitalize="none" autoCorrect="off" data-testid="cash-username" />
            </label>
            <button className="btn btn-primary w-full" disabled={!username} onClick={() => setShowPin(true)} data-testid="cash-submit">Submit My Count</button>
          </div>
        ) : (
          <div className="mt-4">
            <p className="text-center text-sm text-[color:var(--ink-2)] mb-3">{username} — enter PIN to confirm</p>
            <PinPad testid="cash-pinpad" onComplete={submit} />
            <button className="btn btn-ghost w-full mt-3 h-10" onClick={() => setShowPin(false)}>Cancel</button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------------- Review ---------------- */
function Review({ s, reload }) {
  const t = s.computed_totals || {};
  const fundList = (s.funds && s.funds.length ? s.funds : ["General"]);
  const [splits, setSplits] = useState(() => Object.fromEntries(fundList.map((f) => [f, ""])));

  const splitSum = Object.values(splits).reduce((a, v) => a + (Number(v) || 0), 0);
  const balanced = Math.abs(splitSum - (t.grand_total || 0)) < 0.005;

  const autoFill = () => {
    if (fundList.length === 1) setSplits({ [fundList[0]]: (t.grand_total || 0).toFixed(2) });
  };
  useEffect(autoFill, []); // eslint-disable-line

  const confirm = async () => {
    try {
      await api.post(`/sessions/${s.id}/review`, { fund_splits: Object.fromEntries(Object.entries(splits).map(([k, v]) => [k, Number(v) || 0])) });
      toast.success("Review confirmed");
      reload();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-5">
      <div className="card p-6">
        <h2 className="font-serif text-2xl mb-4">Review Totals</h2>
        <div className="grid grid-cols-2 gap-3">
          <Stat label="Cash Total" value={money(t.cash_total)} />
          <Stat label="Check Total" value={money(t.check_total)} />
          <Stat label="Check Count" value={t.check_count ?? 0} />
          <Stat label="Grand Total" value={money(t.grand_total)} highlight />
        </div>
      </div>

      <div className="card p-6">
        <h3 className="font-semibold mb-1">Fund Splits</h3>
        <p className="text-sm text-[color:var(--ink-2)] mb-4">Splits must equal the grand total to submit.</p>
        <div className="space-y-3">
          {fundList.map((f) => (
            <NumField key={f} label={f} prefix="$" value={splits[f]} onChange={(v) => setSplits((s2) => ({ ...s2, [f]: v }))} testid={`split-${f}`} />
          ))}
        </div>
        <div className={`flex justify-between items-center mt-4 p-3 rounded-lg ${balanced ? "bg-[color:var(--olive-light)]" : "bg-[color:var(--terracotta-light)]"}`}>
          <span className="label">Split Total vs Grand</span>
          <span className="font-mono tabnum font-semibold" data-testid="split-total">{money(splitSum)} / {money(t.grand_total)}</span>
        </div>
      </div>

      <button className="btn btn-primary w-full" disabled={!balanced} onClick={confirm} data-testid="confirm-review">Confirm &amp; Proceed <ArrowRight size={18} /></button>
    </div>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div className={`p-4 rounded-lg ${highlight ? "bg-[color:var(--olive)] text-white" : "bg-[color:var(--cream)]"}`}>
      <div className={`text-xs uppercase tracking-wider ${highlight ? "text-white/70" : "text-[color:var(--muted)]"}`}>{label}</div>
      <div className="font-mono tabnum text-xl font-semibold mt-1">{value}</div>
    </div>
  );
}

/* ---------------- Brinks + Signatures ---------------- */
function SignStep({ s, reload }) {
  const brinksDone = !!s.brinks?.bag_serial;
  const [serial, setSerial] = useState("");
  const bagRef = useRef(null);
  const [bagFile, setBagFile] = useState(null);
  const [bagPreview, setBagPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const [signEmail, setSignEmail] = useState("");
  const [sig, setSig] = useState(null);
  const [showPin, setShowPin] = useState(false);

  const onBag = (e) => { const f = e.target.files?.[0]; if (!f) return; setBagFile(f); setBagPreview(URL.createObjectURL(f)); };

  const confirmBrinks = async () => {
    if (!serial) { toast.error("Enter the bag serial"); return; }
    if (!bagFile) { toast.error("Photograph the sealed bag"); return; }
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("photo", bagFile);
      const { data } = await api.post(`/sessions/${s.id}/brinks/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post(`/sessions/${s.id}/brinks`, { bag_serial: serial, bag_photo_path: data.storage_path });
      if (bagPreview) URL.revokeObjectURL(bagPreview);
      toast.success("Brinks drop confirmed");
      reload();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const signedIds = (s.signatures || []).map((x) => x.user_id);
  const bothSigned = signedIds.length >= 2;

  const doSign = async (pin) => {
    if (!sig) { toast.error("Please sign first"); return; }
    try {
      await api.post(`/sessions/${s.id}/sign`, { username: signEmail.trim().toLowerCase(), pin, signature: sig });
      toast.success("Signature recorded");
      setShowPin(false); setSig(null); setSignEmail("");
      reload();
    } catch (e) { toast.error(apiError(e)); }
  };

  const attestation = `I was present for the entire count. The amounts recorded are accurate and complete, and the full deposit was sealed in bag #${s.brinks?.bag_serial || "____"} and placed in the Brinks safe.`;

  return (
    <div className="space-y-5">
      {!brinksDone ? (
        <div className="card p-6">
          <h2 className="font-serif text-2xl mb-1">Brinks Deposit</h2>
          <p className="text-sm text-[color:var(--ink-2)] mb-4">Seal the deposit in a tamper-evident bag, record the serial, photograph it, and drop it in the safe.</p>
          <label className="block mb-4">
            <span className="label block mb-1.5">Bag Serial Number</span>
            <input className="field font-mono" value={serial} onChange={(e) => setSerial(e.target.value)} placeholder="e.g. BR-0093481" data-testid="bag-serial" />
          </label>
          <input ref={bagRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onBag} data-testid="bag-photo-input" />
          {!bagPreview ? (
            <button className="w-full h-40 rounded-xl border-2 border-dashed border-[color:var(--line)] flex flex-col items-center justify-center gap-2 text-[color:var(--muted)]" onClick={() => bagRef.current?.click()} data-testid="capture-bag"><Camera size={28} /><span className="text-sm">Photograph sealed bag</span></button>
          ) : (
            <img src={bagPreview} alt="bag" className="w-full h-44 object-cover rounded-xl border border-[color:var(--line)]" />
          )}
          <button className="btn btn-primary w-full mt-4" disabled={busy} onClick={confirmBrinks} data-testid="confirm-brinks">{busy ? "Saving…" : "Confirm Safe Drop"}</button>
        </div>
      ) : (
        <div className="card p-4 flex items-center gap-3 bg-[color:var(--sage-light)]">
          <Check className="text-[color:var(--olive)]" /><div className="text-sm"><span className="font-semibold">Bag #{s.brinks.bag_serial}</span> sealed &amp; dropped.</div>
        </div>
      )}

      {brinksDone && (
        <div className="card p-6">
          <h2 className="font-serif text-2xl mb-3">Signatures &amp; Attestation</h2>
          <div className="p-4 rounded-lg bg-[color:var(--cream)] text-sm italic text-[color:var(--ink-2)] mb-4">"{attestation}"</div>

          <div className="flex gap-2 mb-4">
            <SigChip label={s.counter1_name} signed={signedIds.includes(s.counter1_id)} />
            <SigChip label={s.counter2_name} signed={signedIds.includes(s.counter2_id)} />
          </div>

          {!bothSigned && (
            <>
              {!showPin ? (
                <div className="space-y-4">
                  <label className="block">
                    <span className="label block mb-1.5">Signer Username</span>
                    <input className="field" value={signEmail} onChange={(e) => setSignEmail(e.target.value.replace(/\s/g, ""))} placeholder="e.g. rancho1" autoCapitalize="none" autoCorrect="off" data-testid="sign-username" />
                  </label>
                  <div>
                    <span className="label block mb-1.5">Signature</span>
                    <SignaturePad testid="signature-pad" onChange={setSig} />
                  </div>
                  <button className="btn btn-primary w-full" disabled={!signEmail || !sig} onClick={() => setShowPin(true)} data-testid="sign-continue">Re-enter PIN to Sign</button>
                </div>
              ) : (
                <div>
                  <p className="text-center text-sm text-[color:var(--ink-2)] mb-3">{signEmail} — confirm PIN to lock signature</p>
                  <PinPad testid="sign-pinpad" onComplete={doSign} />
                  <button className="btn btn-ghost w-full mt-3 h-10" onClick={() => setShowPin(false)}>Cancel</button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function SigChip({ label, signed }) {
  return (
    <div className={`flex-1 p-3 rounded-lg border text-sm ${signed ? "bg-[color:var(--olive-light)] border-[color:var(--olive)]" : "bg-white border-[color:var(--line)]"}`}>
      <div className="flex items-center gap-2">{signed ? <Check size={15} className="text-[color:var(--olive)]" /> : <div className="w-3.5 h-3.5 rounded-full border border-[color:var(--muted)]" />}<span className="font-semibold truncate">{label || "Counter"}</span></div>
      <div className="text-xs text-[color:var(--muted)] mt-0.5">{signed ? "Signed" : "Awaiting signature"}</div>
    </div>
  );
}

/* ---------------- Locked ---------------- */
function Locked({ s }) {
  const t = s.computed_totals || s.totals || {};
  const downloadPdf = async () => {
    try {
      const res = await api.get(`/sessions/${s.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `count_${s.id.slice(0, 8)}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="space-y-5">
      <div className="card p-8 text-center">
        <div className="w-16 h-16 rounded-full bg-[color:var(--olive)] flex items-center justify-center mx-auto mb-4"><Check className="text-white" size={30} /></div>
        <h2 className="font-serif text-3xl mb-1">Session Locked</h2>
        <p className="text-sm text-[color:var(--ink-2)]">This record is now immutable. A signed count sheet was emailed to Finance Admins.</p>
      </div>
      <div className="card p-6 grid grid-cols-2 gap-3">
        <Stat label="Cash Total" value={money(t.cash_total)} />
        <Stat label="Check Total" value={money(t.check_total)} />
        <Stat label="Bag Serial" value={s.brinks?.bag_serial || "—"} />
        <Stat label="Grand Total" value={money(t.grand_total)} highlight />
      </div>
      <button className="btn btn-secondary w-full" onClick={downloadPdf} data-testid="download-pdf"><Download size={18} /> Download Count Sheet (PDF)</button>
    </div>
  );
}
